import { CopilotIframeApi, useCopilotChatStore } from '@pharmsnap/pharmsnap-web/composable/useCopilotChatStore'
import { IS_DEV, VUE_APP_DOMAIN_URL } from '@pharmsnap/pharmsnap-web/config'
import router from '@pharmsnap/pharmsnap-web/router'
import { E_ROUTER_NAME, E_UPGRADE_TYPE } from '@pharmsnap/pharmsnap-web/types/enum'
import { useAuthStore, useChildIframeApi, useExposeToChildIframe } from '@pharmsnap/shared/src/composition'
import { useHiroUsage } from '@pharmsnap/shared/src/composition/useHiroUsage'
import { CHAT_DIALOG_ID } from '@pharmsnap/shared/src/utils/business/copilot'
import { defineComponent, onBeforeUnmount, onMounted, ref } from '@vue/composition-api'
import cn from './locales/cn.json'
import en from './locales/en.json'
import { useCopilotSidebarResize } from './useCopilotSidebarResize'

export const CopilotSideBarDialog = defineComponent({
  name: 'CopilotSideBarDialog',
  props: {
    isAbsolutePosition: {
      default: true,
      type: Boolean,
    },
  },
  i18n: {
    messages: {
      cn,
      en,
    },
  },
  setup() {
    const iframeRef = ref<HTMLIFrameElement | null>(null)
    const copilotIframeUrl = `${IS_DEV ? 'http://localhost:3000' : VUE_APP_DOMAIN_URL}/ls/synapse/copilot`
    const { dialogWidthStyle, isDragging, isExpanded, setExpanded, handleResizeMouseDown, handleResizeMouseMove, handleResizeMouseUp } =
      useCopilotSidebarResize()
    const {
      state: { dialogVisible, placeholderText },
      actions: { showAssistantDialog, questionActions, getPageContext, setCopilotIframeApi, clearSelectedRecordsContext },
    } = useCopilotChatStore()
    const {
      state: { accountExtraInfo },
      actions: { changeUpgrade },
    } = useAuthStore()
    const { loadUsage } = useHiroUsage()

    const parentApi = {
      closeCopilot: () => showAssistantDialog(false),
      getPageContext,
      getRecommendQuestions: () => questionActions.getCurrentQuestions(),
      getPlaceholderText: () => placeholderText.value,
      getUsageDetail: loadUsage,
      open: (url: string, target?: string) => {
        window.open(url, target || '_blank')
      },
      setCopilotSidebarExpanded: (expanded: boolean) => {
        setExpanded(expanded)
      },
      openFeatureUpgradeDialog: () =>
        changeUpgrade({ show: true, type: E_UPGRADE_TYPE.PIA_CHAT_EXCEEDS_MAX_LIMIT, trigger_point: 'synapse_copilot' }),
      getIsInternalUser: () => !!accountExtraInfo.value?.internal,
      clearSelectedRecordsContext,
    }

    // 暴露 API 给子应用调用
    useExposeToChildIframe(parentApi, iframeRef)

    // 包装子应用的 API
    const { proxy: copilotIframeApi } = useChildIframeApi<CopilotIframeApi>(iframeRef, {
      onReady: () => {
        if (copilotIframeApi.value) setCopilotIframeApi(copilotIframeApi.value)
      },
    })

    onMounted(async () => {
      await questionActions.initGeneralQuestions()
      if (router.currentRoute.name) {
        questionActions.updateCurrentPageQuestions(router.currentRoute.name as E_ROUTER_NAME, getPageContext())
        questionActions.updateCurrentPagePlaceholder(router.currentRoute.name as E_ROUTER_NAME, getPageContext())
      }
    })

    onBeforeUnmount(() => {
      setCopilotIframeApi(null)
    })

    return {
      dialogVisible,
      dialogWidthStyle,
      isExpanded,
      isDragging,
      copilotIframeUrl,
      iframeRef,
      handleResizeMouseDown,
      handleResizeMouseMove,
      handleResizeMouseUp,
    }
  },
  render() {
    return (
      <div
        class={[
          'h-full w-full flex justify-end transition-colors',
          this.isAbsolutePosition ? 'absolute right-0 bottom-0 z-[11] top-0' : 'relative z-[4]',
          'pointer-events-none',
        ]}
        v-show={this.dialogVisible}
      >
        {/* overlay 常驻，作用：1.接管拖拽事件，保护鼠标事件不被 iframe 抢走 2.避免拖拽时插入/删除 sibling 导致 iframe 被 Vue diff 重建。 */}
        <div
          class="fixed inset-0 z-[12] cursor-ew-resize pointer-events-auto"
          v-show={this.isDragging}
          onMousemove={this.handleResizeMouseMove}
          onMouseup={this.handleResizeMouseUp}
        ></div>
        <div
          id={CHAT_DIALOG_ID}
          class="relative h-full pointer-events-auto"
          style={[
            {
              width: this.dialogWidthStyle,
              background: 'white',
              boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.08), 0px 1px 4px rgba(0, 0, 0, 0.08)',
              transition: this.isDragging ? 'none' : 'width 0.2s ease',
            },
          ]}
        >
          <div class="absolute left-0 top-0 z-[3] h-full w-3 cursor-ew-resize" onMousedown={this.handleResizeMouseDown}></div>
          <iframe
            ref="iframeRef"
            src={this.copilotIframeUrl}
            class="w-full h-full border-0 block"
            title={this.$t('iframeTitle') as string}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
            allow="clipboard-read; clipboard-write"
          />
        </div>
      </div>
    )
  },
})
